import { Box, ButtonBase, useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

export interface GraphSegmentedOption<T extends string | number> {
    label: string;
    value: T;
}

interface GraphSegmentedControlProps<T extends string | number> {
    options: Array<GraphSegmentedOption<T>>;
    selected: T;
    onSelect: (value: T) => void;
}

// Compact segmented control for the graph filter bar (depth switch, risk-area switch): plain pill buttons
// styled like RiskGovernanceModeSwitcher's, sitting directly on the surrounding paper, but generic over the
// option value type.
function GraphSegmentedControl<T extends string | number>({ options, selected, onSelect }: GraphSegmentedControlProps<T>) {
    const styles = useStyles();

    return (
        <Box sx={styles.group}>
            {options.map((option) => (
                <ButtonBase key={option.value} sx={[styles.button, option.value === selected && styles.selected]} onClick={() => onSelect(option.value)}>
                    {option.label}
                </ButtonBase>
            ))}
        </Box>
    );
}

const useStyles = () => {
    const theme = useTheme();
    return createSxStyles({
        group: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
        },
        button: {
            paddingX: theme.spacing(2.5),
            paddingY: theme.spacing(1),
            borderRadius: "14px",
            fontWeight: theme.typography.fontWeightBold,
            fontSize: "0.85rem",
            color: theme.palette.grey[500],
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        selected: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            outline: "2px solid " + theme.palette.primary.main,
            outlineOffset: "-2px",
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
            },
        },
    });
};

export default GraphSegmentedControl;
